import { useEffect, useRef } from 'react';

import { notificationService } from '@/lib/services/notification.service';

/**
 * Announces a failed load as a toast, once per failure, with the retry attached.
 *
 * Fires on the transition into failure rather than on every render, so a query that keeps
 * retrying in the background does not stack one toast per attempt. Recovering re-arms it, so a
 * later failure is announced again rather than passing in silence.
 *
 * The toast is the announcement, not the explanation: the surface still owes a person something
 * on the page that outlives six seconds and carries the same retry. See `TaxonomyErrorState` for
 * the shape that pairs with this.
 */
export function useLoadFailureToast({
  isError,
  message,
  retryLabel,
  onRetry,
}: {
  isError: boolean;
  message: string;
  retryLabel: string;
  onRetry: () => void;
}): void {
  const announced = useRef(false);

  useEffect(() => {
    if (!isError) {
      announced.current = false;
      return;
    }
    if (announced.current) return;
    announced.current = true;

    notificationService.showError(message, {
      // Two surfaces can watch the same query -- the placement panel and the library dialog it
      // opens both hold one -- and a failure they share is one event, not two.
      preventDuplicate: true,
      action: { label: retryLabel, onClick: onRetry },
    });
  }, [isError, message, retryLabel, onRetry]);
}
